import type { WireProviderOutboxEvent } from '../../api/endpoints.js';
import type { useApiQuery } from '../../api/hooks.js';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  ErrorState,
  Icon,
  LoadingState,
  TBody,
  TD,
  TH,
  THead,
  TRow,
  Table,
} from '../../ui/index.js';
import { TextTokenBreaks } from '../user-inspect/SoftBreaks.js';
import { fmtDate, toneForEvent } from './shared.js';

/**
 * A machine token with soft line-break opportunities between its CamelCase
 * words and after "_" / "/", so a narrow column wraps "ProviderRenewalJob /
 * Enqueued" or "provider_ / renewal_job/…" instead of at an arbitrary letter.
 * Text content is unchanged.
 */
function SoftBreaks({ text }: { readonly text: string }): JSX.Element {
  const parts = text
    .replace(/([a-z0-9])([A-Z])/g, '$1\u0000$2')
    .replace(/([_/])/g, '$1\u0000')
    .split('\u0000')
    .filter((p) => p.length > 0);
  // One wrapper element: a stacked (flex) cell must see a single item, or its
  // gap would paint spaces between the parts.
  return (
    <span>
      {parts.map((p, i) => (
        <span key={i}>
          {i > 0 ? <wbr /> : null}
          {p}
        </span>
      ))}
    </span>
  );
}

export function EventsPanel({
  query,
  onRefresh,
}: {
  readonly query: ReturnType<typeof useApiQuery<{ events: readonly WireProviderOutboxEvent[] }>>;
  readonly onRefresh: () => void;
}): JSX.Element {
  return (
    <Card flush className="mcp-events">
      <CardHeader
        title="Provider Olayları"
        actions={
          <Button variant="ghost" size="sm" onClick={onRefresh} icon={<Icon.Refresh size={12} />}>
            Yenile
          </Button>
        }
      />
      <CardBody>
        <p className="t-tertiary t-small" style={{ margin: '0 0 12px' }}>
          Outbox tablosundan son provider_account / provider_alias / provider_renewal_job eventleri. Hiçbir
          secret/payload alanı UI'da gösterilmez; yalnızca event meta-verisi.
        </p>

        {query.kind === 'loading' ? <LoadingState /> : null}
        {query.kind === 'error' ? <ErrorState reason={query.reason} onRetry={onRefresh} /> : null}
        {query.kind === 'ok' ? (
          (query.data?.events ?? []).length === 0 ? (
            <EmptyState label="Outbox'ta provider event'i yok." />
          ) : (
            <Table dense stack scroll className="mcp-events-table mcp-grid2 tbl-stack-xl">
              <THead>
                <TRow>
                  <TH>Event</TH>
                  <TH>Aggregate</TH>
                  <TH>Durum</TH>
                  <TH>Deneme</TH>
                  <TH>Oluşturulma</TH>
                  <TH>Yayınlandı</TH>
                  <TH>Hata</TH>
                </TRow>
              </THead>
              <TBody>
                {(query.data?.events ?? []).map((e) => (
                  <TRow key={e.id}>
                    <TD className="t-mono t-xs tbl-stack-primary mcp-ev-type" label="Event">
                      <SoftBreaks text={e.eventType} />
                    </TD>
                    <TD className="t-mono t-xs mcp-ev-agg" label="Aggregate">
                      <SoftBreaks text={`${e.aggregateType}/${e.aggregateId.slice(0, 8)}…`} />
                    </TD>
                    <TD className="tbl-stack-status" label="Durum">
                      <Badge tone={toneForEvent(e.status)} dot>{e.status}</Badge>
                    </TD>
                    <TD className="t-mono t-num" label="Deneme">{e.attempts}</TD>
                    <TD className="t-mono t-xs t-tertiary mcp-ev-date" label="Oluşturulma">{fmtDate(e.createdAt)}</TD>
                    <TD className="t-mono t-xs t-tertiary mcp-ev-date" label="Yayınlandı">{fmtDate(e.publishedAt)}</TD>
                    <TD className={e.lastError ? 't-mono t-xs tbl-stack-full' : 't-mono t-xs'} label="Hata" title={e.lastError ?? undefined}>
                      {/* One ellipsised line in the table (full text in the tooltip);
                          wraps in full on the phone card, a long code / URL at its
                          "_" / "." / "/" boundaries (the hints are inert in the
                          one-line table, mailcom.mobile.css). */}
                      <div className="mcp-ev-error">{e.lastError ? <TextTokenBreaks text={e.lastError} /> : '—'}</div>
                    </TD>
                  </TRow>
                ))}
              </TBody>
            </Table>
          )
        ) : null}
      </CardBody>
    </Card>
  );
}
